import { animalSpeciesHelper } from '@farmshare/utils';
import {
  alpha,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import {
  EnumProcessorSchedulingStatus,
  ProcessorScheduling,
} from 'lib/graphql';

import { AttentionInfo } from '../../utils/get-attention-label';
import { getLatestKillDate } from '../../utils/get-latest-kill-date';
import { STATUS_DISPLAY } from '../../shared/styles';

const CATEGORY_LABEL: Record<string, [string, string]> = {
  Splits: ['Split Type', 'Split Types'],
  Buyers: ['Buyer', 'Buyers'],
  Cutsheets: ['Cutsheet', 'Cutsheets'],
};

const SCHEDULED_STATUS = EnumProcessorSchedulingStatus.Scheduled;

type JobCardProps = {
  job: ProcessorScheduling;
  attentionInfo?: AttentionInfo;
};

export function JobCard({ job, attentionInfo }: JobCardProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  const species = animalSpeciesHelper(job.animalSpecies).label;
  const dropoff = job.dropoffDate
    ? moment(job.dropoffDate).utc().format('MMM D')
    : null;
  const latestKillDate = getLatestKillDate(job.animalHeads);
  const harvestDate = latestKillDate
    ? moment(latestKillDate).utc().format('MMM D')
    : null;
  const processor = job.vendor?.shop_name;
  const isScheduled = job.status === SCHEDULED_STATUS;
  const isRequested = job.status === EnumProcessorSchedulingStatus.Requested;
  const errorColor = theme.palette.error.main;

  const totalIssues = attentionInfo?.totalIssues ?? 0;
  const firstCategory = attentionInfo?.firstCategory ?? null;
  const firstCategoryCount = attentionInfo?.firstCategoryCount ?? 0;
  const hasIssues = totalIssues > 0 && !isRequested;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'background.default',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/job/${job._id}`)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        <CardContent
          sx={{
            p: 1.5,
            '&:last-child': { pb: 1.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            {processor && (
              <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
                {processor}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                flexShrink: 0,
                ml: 1,
              }}
            >
              {!attentionInfo && job.status && (
                <Typography variant="caption" color="text.disabled">
                  {STATUS_DISPLAY[job.status] ?? job.status}
                  {job.statusLastUpdated &&
                    ` - ${moment(job.statusLastUpdated).utc().format('MM/DD/YYYY')}`}
                </Typography>
              )}
              {harvestDate && !isRequested && !isScheduled && (
                <Typography variant="caption" color="text.disabled">
                  Harvest Date: {harvestDate}
                </Typography>
              )}
              {(isScheduled || isRequested) && dropoff && (
                <Typography variant="caption" color="text.disabled">
                  Drop Off Date: {dropoff}
                </Typography>
              )}
              {job.jobId && (
                <Typography variant="caption" color="text.disabled">
                  Job ID: {job.jobId}
                </Typography>
              )}
            </Box>
          </Box>

          <Typography variant="body1" color="text.secondary">
            {job.headCount} {species}
          </Typography>
        </CardContent>

        {/* Attention strip */}
        {hasIssues && (
          <Box
            sx={{
              px: 1.5,
              py: { xs: 1, sm: 0.75 },
              bgcolor: alpha(
                errorColor,
                theme.palette.mode === 'dark' ? 0.25 : 0.15,
              ),
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ErrorIcon
              sx={{ color: 'error.main', fontSize: { xs: 22, sm: 20 } }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'error.main',
                lineHeight: 1.4,
                flexShrink: 1,
                minWidth: 0,
              }}
            >
              {firstCategory
                ? `${firstCategoryCount} ${CATEGORY_LABEL[firstCategory][firstCategoryCount === 1 ? 0 : 1]} missing`
                : `${totalIssues} issues need attention`}
            </Typography>
            <Box
              component="span"
              sx={{
                ml: 'auto',
                flexShrink: 0,
                border: 1,
                borderColor: 'error.main',
                borderRadius: 1,
                color: 'error.main',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                py: 0.25,
              }}
            >
              Fix &rarr;
            </Box>
          </Box>
        )}

        {/* Ready strip */}
        {attentionInfo && !hasIssues && !isRequested && (
          <Box
            sx={{
              px: 1.5,
              py: { xs: 1, sm: 0.75 },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CheckCircleIcon
              sx={{ color: 'success.main', fontSize: { xs: 22, sm: 20 } }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'success.main',
                lineHeight: 1.4,
              }}
            >
              Ready
            </Typography>
          </Box>
        )}
      </CardActionArea>
    </Card>
  );
}
